import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppWidget from "@/components/WhatsAppWidget";
import { OrganizationSchema } from "@/components/SEOHead";
import { CITIES, REGIONS } from "@/data/cities";
import { usePageReveals } from "@/hooks/usePageReveals";

const Home = lazy(() => import("@/pages/Home"));
const About = lazy(() => import("@/pages/About"));
const Subletting = lazy(() => import("@/pages/Subletting"));
const Concierge = lazy(() => import("@/pages/Concierge"));
const Properties = lazy(() => import("@/pages/Properties"));
const Owners = lazy(() => import("@/pages/Owners"));
const Contact = lazy(() => import("@/pages/Contact"));
const FAQPage = lazy(() => import("@/pages/FAQ"));
const Blog = lazy(() => import("@/pages/Blog"));
const Article = lazy(() => import("@/pages/Article"));
const Region = lazy(() => import("@/pages/Region"));
const City = lazy(() => import("@/pages/City"));
const CityService = lazy(() => import("@/pages/CityService"));
const Marrakech = lazy(() => import("@/pages/Marrakech"));
const Legal = lazy(() => import("@/pages/Legal"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Accompaniment = lazy(() => import("@/pages/Accompaniment"));
const clientPages = { Home, About, Subletting, Concierge, Properties, Owners, Contact, FAQPage, Blog, Article, Region, City, CityService, Marrakech, Legal, NotFound, Accompaniment };

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const target = decodeURIComponent(hash.slice(1));
      const scrollToAnchor = () => {
        const el = document.getElementById(target);
        if (!el) return false;
        el.scrollIntoView({ behavior: "instant", block: "start" });
        return true;
      };
      if (scrollToAnchor()) return;
      const observer = new MutationObserver(() => { if (scrollToAnchor()) observer.disconnect(); });
      observer.observe(document.getElementById("main-content"), { childList: true, subtree: true });
      return () => observer.disconnect();
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

const Fallback = () => <div className="min-h-screen bg-[#0a0a0a]" aria-busy="true" />;

export function AppContent({ pages = clientPages }) {
  const { Home, About, Subletting, Concierge, Properties, Owners, Contact, FAQPage, Blog, Article, Region, City, CityService, Marrakech, Legal, NotFound, Accompaniment } = pages;
  const { pathname } = useLocation();
  usePageReveals(pathname);
  return (
    <>
      <ScrollToTop />
      <a href="#main-content" data-testid="skip-to-content" className="skip-link">Aller au contenu</a>
      <OrganizationSchema />
      <div className="grain-overlay" aria-hidden="true" />
      <Navbar />
      <main id="main-content" className="overflow-x-clip">
        <Suspense fallback={<Fallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/a-propos" element={<About />} />
            <Route path="/sous-location" element={<Subletting />} />
            <Route path="/conciergerie" element={<Concierge />} />
            <Route path="/nos-biens" element={<Properties />} />
            <Route path="/proprietaires" element={<Owners />} />
            <Route path="/formation-conciergerie" element={<Accompaniment mode="conciergerie" />} />
            <Route path="/formation-sous-location-professionnelle" element={<Accompaniment mode="sousLocation" />} />
            <Route path="/accompagnement-location-courte-duree" element={<Accompaniment mode="proprietaires" />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/conseils" element={<Blog />} />
            <Route path="/conseils/:slug" element={<Article />} />
            <Route path="/marrakech" element={<Marrakech />} />
            {Object.keys(REGIONS).filter((r) => r !== "marrakech").map((slug) => (
              <Route key={slug} path={`/${slug}`} element={<Region slug={slug} />} />
            ))}
            {Object.values(CITIES).map((c) => (
              <Route key={c.slug} path={`/${c.slug}`} element={<City slug={c.slug} />} />
            ))}
            {Object.values(CITIES).flatMap((c) =>
              c.servicePages.map((svc) => (
                <Route key={`${svc}-${c.slug}`} path={`/${svc}-${c.slug}`} element={<CityService slug={c.slug} service={svc} />} />
              ))
            )}
            <Route path="/mentions-legales" element={<Legal section="mentions" />} />
            <Route path="/politique-de-confidentialite" element={<Legal section="confidentialite" />} />
            <Route path="/cgv" element={<Legal section="cgv" />} />
            <Route path="/cookies" element={<Legal section="cookies" />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
      <WhatsAppWidget />
      <Toaster theme="dark" position="bottom-center" toastOptions={{ style: { background: "#141414", border: "1px solid #262626", color: "#f5f3ef" } }} />
    </>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </HelmetProvider>
  );
}
